// `records`: Swift `Record` (GroundTeam/Models/Record.swift), the pins on the map: leads, accounts and contacts.
// Decoding template `Record(name: "")` (GroundTeam/Sync/ResilientDecoding.swift:87).
//
// Eight stored properties are local-only (ResilientDecoding.swift:91): visit counters and "last …" dates are recomputed
// from synced activities on every device and the score is per-device output, so they are never on the wire. A payload
// that still carries them (an old upload) has them ignored here, as iOS strips them before decoding; the console
// derives them itself (WEB_PLAN §3.10).

import { PRIORITIES, RECORD_TYPES, type Priority, type RecordType } from '../enums';
import { readCustomValues, writeCustomValues, type CustomValuesWire } from '../fieldValue';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import { COORDINATE, type CoordinatePayload } from './common';
import {
  BOOL,
  DATE,
  DOUBLE,
  INVALID,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  objectOf,
  type Codec,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

/** Swift `Address` (a non-optional struct on Record; the template holds `Address()`, so every key has a default). */
export interface AddressPayload extends Passthrough {
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export const ADDRESS_KEYS = [
  'street',
  'unit',
  'city',
  'state',
  'postalCode',
  'country',
] as const satisfies readonly (keyof Known<AddressPayload>)[];

/** `Address()` property defaults. */
const ADDRESS_DEFAULTS: Known<AddressPayload> = {
  street: '',
  unit: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
};

export const ADDRESS_FIELDS: FieldMap<AddressPayload> = {
  street: field.req(STRING, () => ''),
  unit: field.req(STRING, () => ''),
  city: field.req(STRING, () => ''),
  state: field.req(STRING, () => ''),
  postalCode: field.req(STRING, () => ''),
  country: field.req(STRING, () => 'US'),
};

export const ADDRESS = objectOf<AddressPayload>('Address', ADDRESS_FIELDS);

/** Reads an Address on its own (missing parts take the `Address()` defaults, unknown keys kept). */
export function decodeAddress(raw: JsonObject): Decoded<AddressPayload> {
  return decodeModel(ADDRESS_FIELDS, raw, { hasId: false });
}

/** A full Address (every key written, as Swift encodes it). */
export function newAddress(init: Partial<AddressPayload> = {}): AddressPayload {
  return createModel(ADDRESS_FIELDS, { ...ADDRESS_DEFAULTS }, init, 'address', { hasId: false });
}

/** `Address.oneLine` (list rows, map callouts, link variables). */
export function addressOneLine(a: Pick<AddressPayload, 'street' | 'unit' | 'city' | 'state' | 'postalCode'>): string {
  const line1 = a.unit === '' ? a.street : `${a.street} ${a.unit}`;
  const cs = [a.city, a.state].filter((s) => s !== '').join(', ');
  const line2 = [cs, a.postalCode].filter((s) => s !== '').join(' ');
  return [line1, line2].filter((s) => s !== '').join(', ');
}

/**
 * `customValues: [UUID: FieldValue]`, on the wire the flat `["<UUID>", FieldValue, …]` array (fieldValue.ts). Read
 * values are kept exactly as received (pair order and key casing included); new payloads get the canonical form.
 */
export const CUSTOM_VALUES: Codec<CustomValuesWire> = {
  label: '[UUID: FieldValue]',
  read(raw, path, issues) {
    return readCustomValues(raw, path, issues) ?? INVALID;
  },
  write(value, path) {
    return writeCustomValues(value, path);
  },
  fallback: () => [],
};

export interface RecordPayload extends Passthrough {
  id: string;
  type: RecordType;
  name: string;
  company: string;
  jobTitle: string;
  address: AddressPayload;
  coordinate?: CoordinatePayload;
  phone: string;
  altPhone: string;
  email: string;
  website: string;
  notes: string;
  pipelineId?: string;
  stageId?: string;
  priority: Priority;
  /** Deal value in the company currency (Double). */
  value: number;
  ownerId?: string;
  territoryId?: string;
  /** Contacts point at their account; a converted lead at the account it became. */
  parentRecordId?: string;
  /** LeadSource id. */
  sourceId?: string;
  tags: string[];
  customValues: CustomValuesWire;
  doNotContact: boolean;
  isArchived: boolean;
  lostReason: string;
  wonAt?: string;
  /** Key in an external CRM (Salesforce / HubSpot), used to de-duplicate imports. */
  externalId: string;
  stageEnteredAt?: string;
  createdBy?: string;
  createdAt: string;
}

/** `Record.localOnlyKeys` (ResilientDecoding.swift:92, same order). */
export const RECORD_LOCAL_ONLY_KEYS = [
  'score',
  'scoreReason',
  'visitCount',
  'lastVisitAt',
  'lastVisitResultId',
  'lastActivityAt',
  'nextScheduledAt',
  'updatedAt',
] as const;

/** Stored properties of Record in declaration order, minus the local-only ones. */
export const RECORD_KEYS = [
  'id',
  'type',
  'name',
  'company',
  'jobTitle',
  'address',
  'coordinate',
  'phone',
  'altPhone',
  'email',
  'website',
  'notes',
  'pipelineId',
  'stageId',
  'priority',
  'value',
  'ownerId',
  'territoryId',
  'parentRecordId',
  'sourceId',
  'tags',
  'customValues',
  'doNotContact',
  'isArchived',
  'lostReason',
  'wonAt',
  'externalId',
  'stageEnteredAt',
  'createdBy',
  'createdAt',
] as const satisfies readonly (keyof Known<RecordPayload>)[];

export const RECORD_FIELDS: FieldMap<RecordPayload> = {
  id: field.strict(UUID),
  type: field.req(enumOf('RecordType', RECORD_TYPES), () => 'lead'),
  name: field.req(STRING, () => ''),
  company: field.req(STRING, () => ''),
  jobTitle: field.req(STRING, () => ''),
  address: field.req(ADDRESS, () => ({ ...ADDRESS_DEFAULTS })),
  coordinate: field.opt(COORDINATE),
  phone: field.req(STRING, () => ''),
  altPhone: field.req(STRING, () => ''),
  email: field.req(STRING, () => ''),
  website: field.req(STRING, () => ''),
  notes: field.req(STRING, () => ''),
  pipelineId: field.opt(UUID),
  stageId: field.opt(UUID),
  priority: field.req(enumOf('Priority', PRIORITIES), () => 'C'),
  value: field.req(DOUBLE, () => 0),
  ownerId: field.opt(UUID),
  territoryId: field.opt(UUID),
  parentRecordId: field.opt(UUID),
  sourceId: field.opt(UUID),
  tags: field.req(arrayOf(STRING), () => []),
  customValues: field.req(CUSTOM_VALUES, () => []),
  doNotContact: field.req(BOOL, () => false),
  isArchived: field.req(BOOL, () => false),
  lostReason: field.req(STRING, () => ''),
  wonAt: field.opt(DATE),
  externalId: field.req(STRING, () => ''),
  stageEnteredAt: field.opt(DATE),
  createdBy: field.opt(UUID),
  createdAt: field.req(DATE, nowWire),
};

/** Decodes a record payload; local-only keys in it are ignored (dropped from the value), as on iOS. */
export function decodeRecord(raw: JsonObject, envelopeId?: string): Decoded<RecordPayload> {
  return decodeModel(RECORD_FIELDS, raw, { localOnly: RECORD_LOCAL_ONLY_KEYS }, envelopeId);
}

export type RecordInit = Omit<Partial<Known<RecordPayload>>, 'address'> & {
  name: string;
  /** Partial is fine: missing parts take the `Address()` defaults. */
  address?: Partial<AddressPayload>;
};

/**
 * A new record with every key at its Swift default. `address` may be partial (the rest is filled from `Address()`);
 * `customValues` must be the flat array (build it with `customValuesFromMap`). Local-only keys are never written.
 */
export function newRecord(init: RecordInit): RecordPayload {
  const { address, ...rest } = init;
  const defaults = {
    id: newId(),
    type: 'lead',
    company: '',
    jobTitle: '',
    address: newAddress(address ?? {}),
    phone: '',
    altPhone: '',
    email: '',
    website: '',
    notes: '',
    priority: 'C',
    value: 0,
    tags: [],
    customValues: [],
    doNotContact: false,
    isArchived: false,
    lostReason: '',
    externalId: '',
    createdAt: nowWire(),
  };
  return createModel(RECORD_FIELDS, defaults, rest, 'records', { localOnly: RECORD_LOCAL_ONLY_KEYS });
}

/** `Record.displayName`. */
export function recordDisplayName(r: Pick<RecordPayload, 'name' | 'company'>): string {
  return r.name !== '' ? r.name : r.company !== '' ? r.company : 'Untitled';
}
